"use server";

import { auth } from "@/auth";
import { isPrivatePath } from "./private-assets";
import { canViewPrivateAsset } from "@/lib/onboarding/media-access";
import { requirePermission } from "@/lib/access";
import {
  listRecentAssets as listRecentAssetsImpl,
  searchAssets as searchAssetsImpl,
  getAssetMetadata as getAssetMetadataImpl,
  type AssetItem,
  type AssetKind,
  type AssetReference,
} from "./assets";

export async function listRecentAssets(): Promise<AssetItem[]> {
  await requirePermission("assets:view");
  return listRecentAssetsImpl();
}

export async function searchAssets(query: string, kind?: AssetKind): Promise<AssetItem[]> {
  await requirePermission("assets:view");
  return searchAssetsImpl(query, kind);
}

export async function getAssetMetadata(assetPath: string): Promise<AssetItem | null> {
  const { userId } = await requirePermission("assets:view");
  if (await isPrivatePath(assetPath)) return (await canViewPrivateAsset(userId, assetPath)) ? getAssetMetadataImpl(assetPath) : null;
  return getAssetMetadataImpl(assetPath);
}

export async function attachExistingAsset(item: AssetItem): Promise<AssetReference> {
  await requirePermission("assets:upload");
  if (await isPrivatePath(item.path)) throw new Error("Not allowed");
  const session = await auth();
  return { ...item, addedAt: new Date().toISOString(), addedBy: session?.user?.name ?? "Unknown" };
}

import { prisma } from "@/lib/prisma";
import { splitExtension } from "./paths";
import { revalidatePath } from "next/cache";

export async function renameAsset(path: string, newBaseName: string): Promise<{ ok: true; name: string } | { ok: false; error: string }> {
  await requirePermission("assets:upload");
  const session = await auth();
  const userId = session?.user?.userId ?? 0;
  
  const trimmed = newBaseName.trim();
  if (!trimmed) return { ok: false, error: "Name cannot be empty" };
  if (trimmed.length > 200) return { ok: false, error: "Name is too long" };
  if (trimmed.includes("/") || trimmed.includes("\\")) return { ok: false, error: "Name cannot contain slashes" };
  
  if (await isPrivatePath(path)) return { ok: false, error: "Asset not found" };
  const item = await getAssetMetadataImpl(path);
  if (!item) return { ok: false, error: "Asset not found" };
  
  const { ext } = splitExtension(item.name);
  const finalName = `${trimmed}${ext}`;
  
  await prisma.assetDisplayName.upsert({
    where: { path },
    update: { name: finalName, updatedBy: userId },
    create: { path, name: finalName, updatedBy: userId },
  });
  
  revalidatePath("/library/assets");
  return { ok: true, name: finalName };
}
